import { SidebarItem } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/frontend-plugin-api';
import FolderSpecialIcon from '@material-ui/icons/FolderSpecial';
import CheckIcon from '@material-ui/icons/Check';
import { Menu, MenuItem, ListItemIcon, Typography } from '@material-ui/core';
import { useEffect, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import type { ProjectView } from '@s2/studio-api-client';
import { useStudioApi } from './studioApi';
import { selectedProjectId, selectProject } from './projectScope';

/**
 * ProjectSwitcher lets the user pick which Studio project the portal is scoped to (evkka, landing,
 * perimeter, first-look, …). It lists the tenant's projects from GET /v1/studio/projects and, on
 * selection, persists the choice and reloads so every page refetches in the new scope. The current
 * project's name is shown in the sidebar so it's always clear WHICH project you're looking at.
 */
export function ProjectSwitcher() {
  const api = useStudioApi();
  const config = useApi(configApiRef);
  const defaultProject = config.getOptionalString('studio.dev.projectId') ?? '';

  const [projects, setProjects] = useState<ProjectView[]>([]);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = selectedProjectId() || defaultProject;

  useEffect(() => {
    let active = true;
    api
      .listTenantProjects()
      .then((list: ProjectView[]) => {
        if (active) setProjects(list);
      })
      .catch(() => {
        if (active) setProjects([]);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentName =
    projects.find(p => p.id === current)?.name ??
    (projects.length ? 'Select project' : 'Loading project…');

  return (
    <>
      <SidebarItem
        icon={FolderSpecialIcon}
        text={currentName}
        onClick={(ev: ReactMouseEvent) => setAnchor(ev.currentTarget as HTMLElement)}
      />
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        <MenuItem disabled>
          <Typography variant="caption">Switch project</Typography>
        </MenuItem>
        {projects.map(p => (
          <MenuItem
            key={p.id}
            selected={p.id === current}
            onClick={() => {
              setAnchor(null);
              if (p.id !== current) selectProject(p.tenant_id, p.id);
            }}
          >
            {p.id === current && (
              <ListItemIcon style={{ minWidth: 28 }}>
                <CheckIcon fontSize="small" />
              </ListItemIcon>
            )}
            <span style={{ marginLeft: p.id === current ? 0 : 28 }}>{p.name}</span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
